/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus, ESmsRole } from '@leedpulse-monorepo/enums';
import SMS from '../../../domain/models/SMS';
import getAllMessages from '../../../domain/use_cases/chat/getAllMessages';
import sendMessage from '../../../domain/use_cases/chat/sendMessage';
import Subscription from '../../../domain/models/Subscription';
import AuthenticationContext from '../auth/AuthenticationContext';
import toggleAIPauseOfContact from '../../../domain/use_cases/contacts/toggleAIPauseOfContact';
import markAsReadAllSMSOfContact from '../../../domain/use_cases/sms/markAsReadAllSMSOfContact';
import createFavorite from '../../../domain/use_cases/favorites/createFavorites';
import toggleSMSReadStatus from '../../../domain/use_cases/sms/toggleSMSReadStatus';
import Job from '../../../domain/models/Job';
import Notifications from '../../../domain/models/Notifications';
import getContactNextJob from '../../../domain/use_cases/contacts/getContactNextJob';
import getContactNotifications from '../../../domain/use_cases/contacts/getContactNotifications';
import deleteJob from '../../../domain/use_cases/jobs/deleteJob';
import createJob from '../../../domain/use_cases/jobs/createJob';
import { message } from 'antd';
import { IContact } from '@leedpulse-monorepo/interfaces';

const ChatContext = createContext({
  items: [] as SMS[],
  currentContact: null as IContact | null,
  currentSubscription: null as Subscription | null,
  nextJob: null as Job | null,
  fetchAllMessages: (_: string) => { },
  notifications: [] as Notifications[],
  sendMessageToContact: (_: string) => { },
  toggleAIPauseForCurrentContact: () => { },
  reset: () => { },
  status: ContextStatus.IDLE,
  handleDeleteJob: (job: Job) => { },
  handleCreateJob: (job: Job) => { },
  aiPauseTogglingStatus: ContextStatus.IDLE,
  sendMessageStatus: ContextStatus.IDLE,
  setAllMessagesAsRead: (onReadAll?: () => void) => { },
  toggleOneSMSReadStatus: (
    sms: SMS,
    onToggle?: (becomeRead: boolean, role: ESmsRole) => void
  ) => { },
  toggleFavoriteBackoffice: () => {},
  copyChatLink: () => {},
});

export const ChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [items, setItems] = useState([] as SMS[]);
  const [currentContact, setCurrentContact] = useState(null as IContact | null);
  const [notifications, setNotifications] = useState([] as Notifications[]);
  const [currentSubscription, setCurrentSubscription] = useState(
    null as Subscription | null
  );
  const [nextJob, setNextJob] = useState(null as Job | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [aiPauseTogglingStatus, setAIPauseTogglingStatus] = useState(
    ContextStatus.IDLE
  );
  const [sendMessageStatus, setSendMessageStatus] = useState(
    ContextStatus.IDLE
  );

  const authContext = useContext(AuthenticationContext);

  const fetchAllMessages = async (contactId: string) => {
    setStatus(ContextStatus.LOADING);

    try {
      const useCaseResponse = await getAllMessages(contactId);
      const nextJob = await getContactNextJob(useCaseResponse.contact);
      const notifications = await getContactNotifications(useCaseResponse.contact);
      setNotifications(notifications ?? []);
      setItems(useCaseResponse.messages);
      setCurrentContact(useCaseResponse.contact);
      setCurrentSubscription(useCaseResponse.subscription);
      setNextJob(nextJob);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const sendMessageToContact = async (message: string) => {
    if (
      !currentContact ||
      !currentSubscription ||
      !authContext.authentication
    ) {
      return;
    }
    setSendMessageStatus(ContextStatus.LOADING);
    try {
      await sendMessage(
        currentContact,
        currentSubscription,
        message,
        authContext.authentication
      );
      fetchAllMessages(currentContact.id);
      setSendMessageStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setSendMessageStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setItems([]);
    setCurrentContact(null);
    setCurrentSubscription(null);
    setNextJob(null);
    setStatus(ContextStatus.IDLE);
    setAIPauseTogglingStatus(ContextStatus.IDLE);
    setSendMessageStatus(ContextStatus.IDLE);
  };

  const toggleAIPauseForCurrentContact = async () => {
    if (
      !currentSubscription ||
      !authContext.authentication ||
      !currentContact
    ) {
      return;
    }
    setAIPauseTogglingStatus(ContextStatus.LOADING);
    try {
      // Set optimistic update
      setCurrentContact((o) => {
        if (!o) return null;
        o.messages_settings.ia_pause = !o.messages_settings.ia_pause;
        return { ...o };
      });
      await toggleAIPauseOfContact(currentContact, authContext.authentication);
      setAIPauseTogglingStatus(ContextStatus.SUCCESS);
    } catch (e) {
      // Revert optimistic update
      setCurrentContact((o) => {
        if (!o) return null;
        o.messages_settings.ia_pause = !o.messages_settings.ia_pause;
        return { ...o };
      });
      setAIPauseTogglingStatus(ContextStatus.ERROR);
    }
  };

  const setAllMessagesAsRead = async (onReadAll?: () => void) => {
    if (!currentContact || !authContext.authentication) {
      return;
    }

    const backupItems = [...items];
    try {
      // Mark all messages as read
      setItems(
        items.map((item) => {
          return item.copyWith({ readedBackoffice: true });
        })
      );
      await markAsReadAllSMSOfContact(
        currentContact,
        authContext.authentication
      );

      // Call the callback
      if (onReadAll) {
        onReadAll();
      }
    } catch (e) {
      // Revert optimistic update
      setItems(backupItems);
    }
  };

  const toggleOneSMSReadStatus = async (
    sms: SMS,
    onToggle?: (becomeRead: boolean, role: ESmsRole) => void
  ) => {
    if (!currentContact || !authContext.authentication) {
      return;
    }

    try {
      // Set optimistic update
      setItems(
        items.map((item) => {
          if (item.id === sms.id) {
            return item.copyWith({ readedBackoffice: !sms.readedBackoffice });
          }
          return item;
        })
      );
      // Call the callback
      if (onToggle) {
        onToggle(!sms.readedBackoffice, sms.role);
      }

      await toggleSMSReadStatus(
        currentContact,
        sms,
        authContext.authentication
      );
    } catch (e) {
      // Revert optimistic update
      setItems(
        items.map((item) => {
          if (item.id === sms.id) {
            return item.copyWith({ readedBackoffice: sms.readedBackoffice });
          }
          return item;
        })
      );

      // Call the callback
      if (onToggle) {
        onToggle(sms.readedBackoffice, sms.role);
      }
    }
  };

  // Handle delete job
  const handleDeleteJob = async (job: Job) => {
    if (!authContext.authentication) {
      return;
    }
    try {
      await deleteJob(job);
      if (currentContact) {
        fetchAllMessages(currentContact.id);
      }
      message.success('Le job a été supprimé avec succès');
      setTimeout(() => {
        window.location.reload();
      }, 1000)
    } catch (error) {
      console.error("Failed to delete job:", error);
      message.error('Une erreur est survenue lors de la suppression du job');
    }
  };

  // Handle create job
  const handleCreateJob = async (job: Job) => {
    if (!currentContact || !authContext.authentication) {
      return;
    }

    try {
      const jobParams = {
        idApiKey: currentContact.id_api_key,
        idContact: currentContact.id,
        type: job.type,
        timestamp: job.timestamp || null,
      };
      await createJob(jobParams as any);
      fetchAllMessages(currentContact.id);
    } catch (error) {
      console.error("Failed to create job:", error);
    }
  };

  // Handle Favorites conversations
  const toggleFavoriteBackoffice = async () => {
    if (!currentContact || !authContext.authentication) return;

    try {
      const newFavoriteStatus = !currentContact.is_favorite_backoffice;

      setCurrentContact(
        (o) => {
          if (!o) return null;
          o.is_favorite_backoffice = newFavoriteStatus;
          return { ...o };
        }
      );

      await createFavorite(currentContact.id, newFavoriteStatus);

      message.success(
        newFavoriteStatus
          ? 'Contact ajouté aux favoris'
          : 'Contact retiré des favoris'
      );
    } catch (error) {
      console.error('Erreur lors de la mise à jour des favoris:', error);

      setCurrentContact(
        (o) => {
          if (!o) return null;
          o.is_favorite_backoffice = currentContact.is_favorite_backoffice;
          return { ...o };
        }
      );

      message.error(
        'Une erreur est survenue lors de la mise à jour des favoris.'
      );
    }
  };

  // Handle copy chat link
  const copyChatLink = () => {
    if (currentContact) {
      const chatLink = `https://admin.leedpulse.com/contacts/${currentContact.id}/chat`;

      navigator.clipboard
        .writeText(chatLink)
        .then(() => {
          message.success('Lien de la conversation copié dans le presse-papiers');
        })
        .catch(() => {
          message.error("Échec de la copie du lien");
        });
    } else {
      message.warning("Aucun contact sélectionné");
    }
  };
  
  return (
    <ChatContext.Provider
      value={{
        items,
        currentContact,
        currentSubscription,
        nextJob,
        notifications,
        fetchAllMessages,
        sendMessageToContact,
        toggleAIPauseForCurrentContact,
        reset,
        status,
        aiPauseTogglingStatus,
        sendMessageStatus,
        setAllMessagesAsRead,
        toggleOneSMSReadStatus,
        handleCreateJob,
        handleDeleteJob,
        toggleFavoriteBackoffice,
        copyChatLink
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
